'use client';

import { useEffect, useRef } from 'react';
import styles from './LandingHome.module.css';
import { prefersReducedMotion } from './landingData';

type Node = { id: string; title: string; layer: number; row: number; pre: string[] };

/** An illustrative Rutgers CS + math path (real course numbers; prerequisites simplified). */
const NODES: Node[] = [
  { id: '640:151', title: 'Calculus I', layer: 0, row: 0, pre: [] },
  { id: '198:111', title: 'Intro to Computer Science', layer: 0, row: 2, pre: [] },
  { id: '640:152', title: 'Calculus II', layer: 1, row: 0, pre: ['640:151'] },
  { id: '198:112', title: 'Data Structures', layer: 1, row: 2, pre: ['198:111'] },
  { id: '198:205', title: 'Discrete Structures I', layer: 1, row: 3, pre: ['198:111', '640:151'] },
  { id: '198:211', title: 'Computer Architecture', layer: 1, row: 1, pre: ['198:111'] },
  { id: '640:250', title: 'Linear Algebra', layer: 2, row: 0, pre: ['640:152'] },
  { id: '198:206', title: 'Discrete Structures II', layer: 2, row: 3, pre: ['198:205'] },
  { id: '198:213', title: 'Software Methodology', layer: 2, row: 2, pre: ['198:112'] },
  { id: '198:314', title: 'Programming Languages', layer: 2, row: 1, pre: ['198:112', '198:211'] },
  { id: '640:251', title: 'Multivariable Calculus', layer: 3, row: 0, pre: ['640:152'] },
  { id: '198:344', title: 'Algorithms', layer: 3, row: 3, pre: ['198:112', '198:206'] },
  { id: '198:336', title: 'Information & Data Management', layer: 3, row: 2, pre: ['198:112', '198:206'] },
  { id: '198:416', title: 'Operating Systems', layer: 3, row: 1, pre: ['198:211', '198:112'] },
  { id: '198:440', title: 'Intro to Artificial Intelligence', layer: 4, row: 2, pre: ['198:344', '640:250'] },
];
const LAYERS = 5, ROWS = 4;
const byId = new Map(NODES.map((n) => [n.id, n]));
const children = new Map<string, string[]>(NODES.map((n) => [n.id, []]));
NODES.forEach((n) => n.pre.forEach((p) => children.get(p)?.push(n.id)));

/** Every course on the path to `id` (ancestors) and every course it unlocks (descendants). */
function chain(id: string) {
  const out = new Set<string>([id]);
  const up = (k: string) => byId.get(k)?.pre.forEach((p) => { if (!out.has(p)) { out.add(p); up(p); } });
  const down = (k: string) => children.get(k)?.forEach((c) => { if (!out.has(c)) { out.add(c); down(c); } });
  up(id); down(id);
  return out;
}

/** Courses as stars, prerequisites as lines; the pointer (or an idle tour) lights one course's full chain. */
export default function ConstellationBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const reduce = prefersReducedMotion();
    let W = 0, H = 0, raf = 0, visible = true;
    let pos = new Map<string, { x: number; y: number }>();
    let focus: string | null = null;
    let pointer: { x: number; y: number } | null = null;
    let tourAt = 0, tourIdx = 3;
    let lit = new Set<string>();
    const glowAmt = new Map<string, number>(NODES.map((n) => [n.id, 0]));

    const layout = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const narrow = W < 700;
      const x0 = W * (narrow ? 0.08 : 0.06), x1 = W * (narrow ? 0.92 : 0.94);
      const y0 = H * 0.14, y1 = H * (narrow ? 0.42 : 0.48);
      pos = new Map(NODES.map((n) => {
        const jitter = (Math.sin(n.layer * 7.3 + n.row * 3.1) * 0.5) * (y1 - y0) * 0.05;
        return [n.id, { x: x0 + (x1 - x0) * (n.layer / (LAYERS - 1)), y: y0 + (y1 - y0) * (n.row / (ROWS - 1)) + jitter }];
      }));
    };

    const nearest = () => {
      if (!pointer) return null;
      let best: string | null = null, bd = 110;
      pos.forEach((p, id) => { const d = Math.hypot(p.x - pointer!.x, p.y - pointer!.y); if (d < bd) { bd = d; best = id; } });
      return best;
    };

    const draw = (now: number) => {
      const hover = nearest();
      if (hover) { focus = hover; tourAt = now; }
      else if (now - tourAt > 2600) { tourIdx = (tourIdx + 1) % NODES.length; focus = NODES[tourIdx].id; tourAt = now; }
      lit = focus ? chain(focus) : new Set();
      ctx.fillStyle = '#0b0203';
      ctx.fillRect(0, 0, W, H);
      // faint dust
      for (let i = 0; i < 70; i++) {
        const x = (Math.sin(i * 91.7) * 0.5 + 0.5) * W, y = (Math.sin(i * 47.3 + 1) * 0.5 + 0.5) * H;
        ctx.fillStyle = `rgba(255,214,208,${0.05 + 0.05 * Math.sin(now / 1500 + i)})`;
        ctx.fillRect(x, y, 2, 2);
      }
      // edges
      NODES.forEach((n) => n.pre.forEach((pid) => {
        const a = pos.get(pid)!, b = pos.get(n.id)!;
        const on = lit.has(pid) && lit.has(n.id);
        const mx = (a.x + b.x) / 2, bend = (b.y - a.y) * 0.15;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, (a.y + b.y) / 2 - bend, b.x, b.y);
        ctx.strokeStyle = on ? 'rgba(255,42,79,.9)' : 'rgba(255,214,208,.34)';
        ctx.lineWidth = on ? 1.8 : 1;
        ctx.stroke();
        if (on && !reduce) { // a prerequisite "signal" travelling along the line
          const k = (now / 1400 + a.x * 0.003) % 1;
          const px = (1 - k) * (1 - k) * a.x + 2 * (1 - k) * k * mx + k * k * b.x;
          const py = (1 - k) * (1 - k) * a.y + 2 * (1 - k) * k * ((a.y + b.y) / 2 - bend) + k * k * b.y;
          ctx.fillStyle = '#fff4ee';
          ctx.fillRect(px - 1.5, py - 1.5, 3, 3);
        }
      }));
      // nodes + labels
      ctx.textBaseline = 'middle';
      NODES.forEach((n) => {
        const p = pos.get(n.id)!;
        const on = lit.has(n.id);
        const g = (glowAmt.get(n.id) ?? 0) + ((on ? 1 : 0) - (glowAmt.get(n.id) ?? 0)) * 0.18;
        glowAmt.set(n.id, g);
        if (g > 0.02) {
          const r = 16 + g * 10;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
          grad.addColorStop(0, `rgba(255,42,79,${0.55 * g})`);
          grad.addColorStop(1, 'rgba(255,42,79,0)');
          ctx.fillStyle = grad;
          ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3 + g * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = n.id === focus ? '#fff4ee' : g > 0.5 ? '#ff2a4f' : 'rgba(255,244,238,.9)';
        ctx.fill();
        const narrow = W < 700;
        ctx.font = `${narrow ? 9 : 11}px ui-monospace, "SF Mono", Menlo, monospace`;
        ctx.fillStyle = g > 0.5 ? 'rgba(255,244,238,.95)' : 'rgba(255,214,208,.62)';
        ctx.fillText(`01:${n.id}`, p.x + 9, p.y - (narrow ? 0 : 7));
        if (n.id === focus && !narrow) {
          ctx.font = '600 13px Inter, system-ui, sans-serif';
          ctx.fillStyle = '#fff4ee';
          ctx.fillText(n.title, p.x + 9, p.y + 9);
        }
      });
    };

    const frame = (now: number) => {
      raf = 0;
      if (!visible || document.hidden) return;
      draw(now);
      if (!reduce) raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!raf) raf = requestAnimationFrame(frame); };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer = e.clientY >= r.top && e.clientY <= r.bottom ? { x: e.clientX - r.left, y: e.clientY - r.top } : null;
      if (reduce) draw(performance.now());
    };
    layout();
    start();
    const onResize = () => { layout(); if (reduce) draw(performance.now()); };
    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove, { passive: true });
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; start(); });
    io.observe(canvas);
    document.addEventListener('visibilitychange', start);
    return () => { if (raf) cancelAnimationFrame(raf); window.removeEventListener('pointermove', onMove); window.removeEventListener('resize', onResize); io.disconnect(); document.removeEventListener('visibilitychange', start); };
  }, []);

  return (
    <>
      <canvas ref={ref} className={styles.canvas} aria-hidden="true" />
      <p className={styles.backdropCaption} aria-hidden="true">Example degree path · hover a course to see what leads to it</p>
    </>
  );
}
